import { Wallet } from 'lucide-react';
import { formatMoneyTotal } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Stat } from '../../../ui/Stat';
import { displayStatus, hoursMinutesText, moneyShort, useBillingMode } from '../billing-format';

/** Podsumowanie rozliczeń klienta (`billing.clientSummary`), np. do kafelków profilu klienta. */
export function useClientBillingSummary(clientId: string, enabled = true) {
  const access = useAccess();
  const allowed = access.can('billing') || access.can('time');
  return trpc.billing.clientSummary.useQuery({ clientId }, { enabled: enabled && allowed });
}

/**
 * Kafelek „Do rozliczenia” profilu klienta (Figma 05 Profil REA): niezafakturowany czas klienta — kwota z „Widzi
 * stawki i kwoty”, inaczej godziny; notatka: czas albo ostatnie zestawienie. Klik → Rozliczenia z filtrem klienta
 * (z modułem Rozliczenia). Bez modułu Czas i Rozliczenia nic nie renderuje.
 */
export function ClientBillingStat({ clientId, className }: { clientId: string; className?: string }) {
  const access = useAccess();
  const query = useClientBillingSummary(clientId);
  // rozliczenia z klientem (bez systemu faktur): „wysłane do klienta” zamiast „wystawiona, nieopłacona”
  const { settlement } = useBillingMode(access.can('billing'));
  if (!access.can('billing') && !access.can('time')) return null;
  const to = access.can('billing') ? `/rozliczenia?klient=${clientId}` : undefined;
  if (query.isPending || query.isError || !query.data) {
    return (
      <Stat
        label="Do rozliczenia"
        icon={Wallet}
        value="—"
        showDot={false}
        note={query.isError ? 'nie udało się wczytać' : 'wczytywanie…'}
        className={className}
      />
    );
  }
  const { unbilled, lastDraft } = query.data;
  const showAmount = access.flag('seesRates') && unbilled.amount !== undefined;
  // wszystko w zestawieniu roboczym (np. seed REA): pokaż, ile czeka w zestawieniu, zamiast samego statusu
  const inWorkingDraft =
    lastDraft?.status === 'draft'
      ? `w zestawieniu: ${
          showAmount && lastDraft.totalNet !== undefined
            ? moneyShort(lastDraft.totalNet, lastDraft.currency)
            : hoursMinutesText(lastDraft.totalSeconds)
        }`
      : null;
  const last =
    inWorkingDraft ??
    (lastDraft ? `ostatnie: ${displayStatus(lastDraft, settlement).label.toLocaleLowerCase('pl-PL')}` : null);
  return (
    <Stat
      label="Do rozliczenia"
      icon={Wallet}
      value={
        showAmount
          ? formatMoneyTotal({ ...unbilled, amount: unbilled.amount as string }, { short: true })
          : hoursMinutesText(unbilled.seconds)
      }
      note={
        unbilled.entries === 0
          ? (last ?? 'wszystko rozliczone')
          : showAmount
            ? hoursMinutesText(unbilled.seconds)
            : 'czasu do rozliczenia'
      }
      showDot={false}
      to={to}
      className={className}
    />
  );
}
